import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { EstimateTotal } from '../../models/estimates/estimate-total';
import { EstimateTotalService } from '../../services/estimate-total.service';

@Component({
  selector: 'estimate-total',
  templateUrl: './estimate-total.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EstimateTotalComponent implements OnChanges, OnInit, OnDestroy {
  @Input() estimateTotal: EstimateTotal;
  @Input() horizontal: boolean = false;
  @Input() localMode: boolean = false; // When true, uses input binding; when false, uses global service

  private readonly _destroying$ = new Subject<void>();

  constructor(private estimateTotalService: EstimateTotalService,
              private changeDetectorRef: ChangeDetectorRef) {}

  // Public properties for template binding
  public laborTotal: number = 0;
  public laborTaxPercentage: number = 0;
  public laborTaxTotal: number = 0;
  public laborTotalWithTax: number = 0;
  
  public partTotal: number = 0;
  public partTaxPercentage: number = 0;
  public partTaxTotal: number = 0;
  public partTotalWithTax: number = 0;
  
  public supplyTotal: number = 0;
  public supplyTaxPercentage: number = 0;
  public supplyTaxTotal: number = 0;
  public supplyTotalWithTax: number = 0;
  
  public total: number = 0;
  public totalTax: number = 0;
  public totalWithTax: number = 0;

  ngOnInit(): void {
    // Only subscribe to global service when NOT in local mode
    if (!this.localMode) {
      this.estimateTotalService.estimateTotal$
        .pipe(takeUntil(this._destroying$))
        .subscribe(estimateTotal => {
          this.updateCalculatedValuesFromService(estimateTotal);
          this.changeDetectorRef.markForCheck();
        });
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // In local mode, always update from input binding when estimateTotal changes
    if (this.localMode && changes['estimateTotal'] && this.estimateTotal) {
      this.updateCalculatedValues();
    }
    // In global mode, only update if input has data (backward compatibility)
    else if (!this.localMode && changes['estimateTotal'] && this.estimateTotal &&
        (this.estimateTotal.laborTotal > 0 || this.estimateTotal.partTotal > 0 || this.estimateTotal.supplyTotal > 0)) {
      this.updateCalculatedValues();
    }
  }

  private updateCalculatedValues(): void {
    // Basic values from input - for backward compatibility
    this.laborTotal = this.estimateTotal?.laborTotal ?? 0;
    this.laborTaxTotal = this.estimateTotal?.laborTaxTotal ?? 0;
    this.laborTaxPercentage = (this.estimateTotal?.laborTaxPercentage ?? 0) / 100; // Convert to decimal

    this.partTotal = this.estimateTotal?.partTotal ?? 0;
    this.partTaxTotal = this.estimateTotal?.partTaxTotal ?? 0;
    this.partTaxPercentage = (this.estimateTotal?.partTaxPercentage ?? 0) / 100; // Convert to decimal

    this.supplyTotal = this.estimateTotal?.supplyTotal ?? 0;
    this.supplyTaxTotal = this.estimateTotal?.supplyTaxTotal ?? 0;
    this.supplyTaxPercentage = (this.estimateTotal?.supplyTaxPercentage ?? 0) / 100; // Convert to decimal

    // Calculated values
    this.laborTotalWithTax = this.laborTotal + this.laborTaxTotal;
    this.partTotalWithTax = this.partTotal + this.partTaxTotal;
    this.supplyTotalWithTax = this.supplyTotal + this.supplyTaxTotal;

    this.total = this.laborTotal + this.partTotal + this.supplyTotal;
    this.totalTax = this.laborTaxTotal + this.partTaxTotal + this.supplyTaxTotal;
    this.totalWithTax = this.laborTotalWithTax + this.partTotalWithTax + this.supplyTotalWithTax;
  }

  private updateCalculatedValuesFromService(estimateTotal: EstimateTotal): void {
    // Basic values from service
    this.laborTotal = estimateTotal?.laborTotal ?? 0;
    this.laborTaxTotal = estimateTotal?.laborTaxTotal ?? 0;
    this.laborTaxPercentage = estimateTotal?.laborTaxPercentage ?? 0; // Don't divide by 100 - service already provides decimal

    this.partTotal = estimateTotal?.partTotal ?? 0;
    this.partTaxTotal = estimateTotal?.partTaxTotal ?? 0;
    this.partTaxPercentage = estimateTotal?.partTaxPercentage ?? 0; // Don't divide by 100 - service already provides decimal

    this.supplyTotal = estimateTotal?.supplyTotal ?? 0;
    this.supplyTaxTotal = estimateTotal?.supplyTaxTotal ?? 0;
    this.supplyTaxPercentage = estimateTotal?.supplyTaxPercentage ?? 0; // Don't divide by 100 - service already provides decimal

    // Calculated values
    this.laborTotalWithTax = this.laborTotal + this.laborTaxTotal;
    this.partTotalWithTax = this.partTotal + this.partTaxTotal;
    this.supplyTotalWithTax = this.supplyTotal + this.supplyTaxTotal;

    this.total = this.laborTotal + this.partTotal + this.supplyTotal;
    this.totalTax = this.laborTaxTotal + this.partTaxTotal + this.supplyTaxTotal;
    this.totalWithTax = this.laborTotalWithTax + this.partTotalWithTax + this.supplyTotalWithTax;
  }
} 